import React from 'react';
import { graphql, navigate } from 'gatsby';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

import Container from '@/components/atoms/Container';
import Typography from '@/components/atoms/Typography';
import Grid from '@/components/atoms/Grid';
import Img from '@/components/atoms/Img';
import Button from '@/components/atoms/Button';

import ImageDataType from '@/types/data/image';
import mq from '@/styles/mq';

import Eau from '../../../static/eau.svg';
import Solvant from '../../../static/solvant.svg';

interface Props {
  name?: React.ReactNode;
  image?: ImageDataType;
  mainFeatures?: React.ReactNode;
  description?: React.ReactNode;
  areaOfUse?: React.ReactNode;
  technical?: string;
  composition?: string;
  liter?: string;
  external_site_products?: string;
}

const ProductHeaderRoot = styled('div')`
  ${mq('lg')} {
    padding-top: ${({ theme }) => theme.spacing(10)};
    background: linear-gradient(
      90deg,
      #fff 50%,
      ${({ theme }) => theme.color.secondary.main} 50%
    );
  }
`;
const ProductHeaderContainer = styled(Container)`
  max-width: 100%;
  padding: 0;
  position: relative;
  ${mq('lg')} {
    max-width: ${({ theme }) => theme.breakpoints.xl}px;
  }
`;
const LeftSideGrid = styled(Grid)`
  order: 3;
  padding-top: ${({ theme }) => theme.spacing(5)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(5)};
  justify-content: center;
  align-items: center;
  ${mq('lg')} {
    height: 100vh;
    order: 1;
    padding-right: 0;
    padding-top: 0;
    padding-bottom: 0;
    justify-content: left;
  }
`;

const ListItem = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  font-size: 1.6rem;
`;
const ListIcon = styled(FontAwesomeIcon)`
  font-size: 2rem;
  margin-right: ${({ theme }) => theme.spacing(1)};
`;
const CenterGrid = styled(Grid)`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding-top: ${({ theme }) => theme.spacing(10)};
  order: 1;
  background-color: ${({ theme }) => theme.color.secondary.main};

  ${mq('lg')} {
    height: 100vh;
    order: 2;
    padding-top: 0;
    background-color: initial;
  }
`;
const ProductImage = styled(Img)`
  width: 300px;
  ${mq('md')} {
    width: 400px;
  }
`;
const RightSideGrid = styled(Grid)`
  background-color: ${({ theme }) => theme.color.secondary.main};
  align-items: center;
  justify-content: center;
  order: 2;
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2.5)};

  ${mq('lg')} {
    justify-content: flex-end;
    height: 100vh;
    order: 3;
    padding-left: 0;
    padding-bottom: 0;
  }
`;
const RightSideContent = styled('div')`
  max-width: 500px;
  text-align: center;
  ${mq('xs')} {
    text-align: center;
  }

  ${mq('lg')} {
    text-align: left;
    max-width: 350px;
  }
`;
const Description = styled(Typography)``;
const AreaOfUse = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;
const TechnicalButton = styled(Button)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;
const Discover = styled(Button)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;
const BackButton = styled.button`
  background-color: transparent;
  position: absolute;
  top: 50px;
  left: center;
  ${mq('lg')} {
    /* position: initial; */
    top: 30px;
  }
`;

const CompositionContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  min-height: 70px;
  > svg {
    width: 70px;
    height: 70px;
  }
`;
const LiterContainer = styled(Typography)`
  font-weight: bold;
`;

const SvgComposition = (type: string): JSX.Element => {
  switch (type) {
    case 'eau':
      return <Eau />;
    case 'solvant':
      return <Solvant />;
    default:
      return <div />;
  }
};

const ProductHeader = ({
  name,
  mainFeatures,
  image,
  description,
  areaOfUse,
  technical,
  composition,
  liter,
  external_site_products,
}: Props): JSX.Element => {
  // @ts-ignore
  const MainFeatures = mainFeatures?.props?.render?.map(
    (feature: any, index: number) => {
      return (
        <ListItem color="textPrimary" variant="textMd" key={index.toString()}>
          <ListIcon icon={faCheckCircle} /> {feature?.text}
        </ListItem>
      );
    },
  );

  return (
    <ProductHeaderRoot>
      <ProductHeaderContainer>
        <Grid container>
          <LeftSideGrid xxs={12} lg={4} container>
            <BackButton onClick={() => navigate(-1)}>
              <FontAwesomeIcon size="2x" icon={faArrowLeft} />
            </BackButton>
            <div>{MainFeatures}</div>
          </LeftSideGrid>

          <CenterGrid xxs={12} lg={4}>
            <Typography variant="h1">{name}</Typography>
            {image?.url && (
              <ProductImage
                src={image?.url}
                alt={image?.alt || ''}
                //width={400}
                //height={520}
              />
            )}
            <LiterContainer variant="textLg">{liter}</LiterContainer>
          </CenterGrid>

          <RightSideGrid xxs={12} lg={3} container>
            <RightSideContent>
              <CompositionContainer>
                {composition !== null && SvgComposition(composition as string)}
              </CompositionContainer>
              <Description variant="textLg">{description}</Description>
              <AreaOfUse color="textPrimary">{areaOfUse}</AreaOfUse>
              <TechnicalButton>
                <a href={technical} target="_blank" download rel="noreferrer">
                  Télécharger la fiche technique
                </a>
              </TechnicalButton>
              {external_site_products && (
                <a
                  href={external_site_products}
                  target="_blank"
                  download
                  rel="noreferrer"
                >
                  <Discover variant="outlined">
                    Découvrez d'autres produits
                  </Discover>
                </a>
              )}
            </RightSideContent>
          </RightSideGrid>
        </Grid>
      </ProductHeaderContainer>
    </ProductHeaderRoot>
  );
};

export const query = graphql`
  fragment ProductHeader on PrismicProductDataType {
    composition
    liter
    external_site_products
    name {
      raw
    }
    image {
      url
      alt
    }
    main_features {
      raw
    }
    description {
      raw
    }
    area_of_use {
      raw
    }
    technical_sheet {
      url
    }
  }
`;

export default ProductHeader;
